import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { businessRuleOptions } from "../data/businessRuleOptions";
import AmountGridTable from "./AmountGridTable";

const sectionTitleSx = {
  color: "#7A7A7A",
  fontWeight: "600",
  fontSize: "14px",
  my: 0.5,
};

const footnoteTextSx = {
  fontSize: "8px",
  color: "#7A7A7A",
  fontWeight: "400",
  ml: 1,
  my: 1,
};

export default function BusinessRule() {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const emptyWageRow = id => ({
    id,
    nomenclature: "",
    wageItemType: "",
    irrf: "",
    inss: "",
    fgts: "",
    averages: "",
    incorporateSalary: "",
  });

  const initRows = namespace => {
    const currentValues = watch(namespace);
    if (!currentValues || Object.keys(currentValues).length === 0) {
      return [emptyWageRow(0)];
    }
    return Object.entries(currentValues).map(([id, values]) => ({
      id: parseInt(id),
      ...values,
    }));
  };

  const [specificWageRows, setSpecificWageRows] = useState(() => initRows("specificWageRows"));
  const [customWageRows, setCustomWageRows] = useState(() => initRows("customWageRows"));

  const makeAddRow = (rows, setRows, namespace) => () => {
    const newId = rows.length;
    const newRow = emptyWageRow(newId);
    setRows(prev => [...prev, newRow]);
    Object.entries(newRow).forEach(([key, value]) => {
      if (key !== "id") {
        setValue(`${namespace}.${newId}.${key}`, value, { shouldValidate: false });
      }
    });
  };

  const makeDeleteRow = (rows, setRows) => id => {
    setRows(rows.filter(row => row.id !== id));
  };

  const handleAddSpecificWageRow = makeAddRow(specificWageRows, setSpecificWageRows, "specificWageRows");
  const handleDeleteSpecificWageRow = makeDeleteRow(specificWageRows, setSpecificWageRows);
  const handleAddCustomWageRow = makeAddRow(customWageRows, setCustomWageRows, "customWageRows");
  const handleDeleteCustomWageRow = makeDeleteRow(customWageRows, setCustomWageRows);

  const businessRuleIsValidated = watch("businessRuleIsValidated");
  const applyCollectiveAgreementRules = watch("applyCollectiveAgreementRules");
  const vacationAdvanceDiscounts = watch("vacationAdvanceDiscounts");
  const vacationAverages = watch("vacationAverages");
  const vacationBenefits = watch("vacationBenefits");
  const terminationDiscounts = watch("terminationDiscounts");
  const loanDiscountPercentageTermination = watch("loanDiscountPercentageTermination");
  const terminationAverages = watch("terminationAverages");
  const payrollHasInterns = watch("payrollHasInterns");
  const internSalaryAdjustmentRule = watch("internSalaryAdjustmentRule");
  const internHasDeductions = watch("internHasDeductions");
  const internHasBenefits = watch("internHasBenefits");
  const internFixedBenefit = watch("internFixedBenefit");
  const internProportionalCalcRule = watch("internProportionalCalcRule");
  const payrollHasApprentices = watch("payrollHasApprentices");
  const apprenticeSalaryAdjustmentRule = watch("apprenticeSalaryAdjustmentRule");
  const apprenticeHasDeductions = watch("apprenticeHasDeductions");
  const apprenticeHasBenefits = watch("apprenticeHasBenefits");
  const apprenticeFixedBenefit = watch("apprenticeFixedBenefit");
  const apprenticeProportionalCalcRule = watch("apprenticeProportionalCalcRule");
  const hasAutonomous = watch("hasAutonomous");
  const hasProLabore = watch("hasProLabore");
  const proLaboreSalaryAdjustmentRule = watch("proLaboreSalaryAdjustmentRule");
  const hasDomesticEmployee = watch("hasDomesticEmployee");
  const domesticEmployeeSalaryAdjustmentRule = watch("domesticEmployeeSalaryAdjustmentRule");
  const domesticEmployeeClosePeriod = watch("domesticEmployeeClosePeriod");
  const hasSpecificWageItems = watch("hasSpecificWageItems");
  const hasCustomWageIncidences = watch("hasCustomWageIncidences");
  const hasHazardPay = watch("hasHazardPay");
  const hasInsalubrityPay = watch("hasInsalubrityPay");
  const otherExclusiveRules = watch("otherExclusiveRules");

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        mx: { xs: 0, sm: 1, md: 2 },
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Regra de Negócio
        </Typography>

        <Controller
          name="businessRuleIsValidated"
          control={control}
          render={({ field }) => (
            <FormControl required error={!!errors.businessRuleIsValidated}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox {...field} checked={!!field.value} size="small" />
                <Typography variant="body2">Validado</Typography>
              </Box>
              <FormHelperText>
                {errors.businessRuleIsValidated?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
      </Box>

      <Grid container spacing={1.5}>
        {/* collectiveAgreementType — sem dependente */}
        <Grid item xs={12}>
          <Controller
            name="collectiveAgreementType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.collectiveAgreementType}>
                <InputLabel id="collective-agreement-type-label">
                  Acordo Coletivo de Trabalho (ACT) ou Convenção Coletiva de Trabalho (CCT)?
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="collective-agreement-type-label"
                  label="Acordo Coletivo de Trabalho (ACT) ou Convenção Coletiva de Trabalho (CCT)?"
                >
                  {businessRuleOptions.collectiveAgreementType.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.collectiveAgreementType?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {/* applyCollectiveAgreementRules — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={["2", "4"].includes(applyCollectiveAgreementRules) ? 6 : 12}>
              <Controller
                name="applyCollectiveAgreementRules"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.applyCollectiveAgreementRules}>
                    <InputLabel id="apply-collective-agreement-rules-label">
                      A empresa aplica as regras da ACT|CCT
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="apply-collective-agreement-rules-label"
                      label="A empresa aplica as regras da ACT|CCT"
                    >
                      {businessRuleOptions.applyCollectiveAgreementRules.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.applyCollectiveAgreementRules?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {applyCollectiveAgreementRules === "2" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="collectiveAgreementPartialClauses"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Informar as cláusulas que não seguem"
                      size="small"
                      fullWidth
                      error={!!errors.collectiveAgreementPartialClauses}
                      helperText={errors.collectiveAgreementPartialClauses?.message}
                    />
                  )}
                />
              </Grid>
            )}
            {applyCollectiveAgreementRules === "4" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="applyCollectiveAgreementRulesOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Descreva detalhadamente a regra de negócio da empresa"
                      size="small"
                      fullWidth
                      error={!!errors.applyCollectiveAgreementRulesOther}
                      helperText={errors.applyCollectiveAgreementRulesOther?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* vacationAdvanceDiscounts — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={vacationAdvanceDiscounts === "1" ? 6 : 12}>
              <Controller
                name="vacationAdvanceDiscounts"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.vacationAdvanceDiscounts}>
                    <InputLabel id="vacation-advance-discounts-label">
                      Antecipa descontos no cálculo de férias
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="vacation-advance-discounts-label"
                      label="Antecipa descontos no cálculo de férias"
                    >
                      {businessRuleOptions.vacationAdvanceDiscounts.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.vacationAdvanceDiscounts?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {vacationAdvanceDiscounts === "1" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="vacationAdvanceDiscountsList"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Sinalize: (Ex.: Plano de Saúde, Plano Odontológico, Empréstimos...)"
                      size="small"
                      fullWidth
                      error={!!errors.vacationAdvanceDiscountsList}
                      helperText={errors.vacationAdvanceDiscountsList?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {vacationAdvanceDiscounts === "1" && (
          <Grid item xs={12}>
            <Controller
              name="vacationDiscountConditions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required disabled={businessRuleIsValidated}
                  label="Condições para aplicar o desconto - (ex.: 30 dias de gozo, limite de X dias)"
                  size="small"
                  fullWidth
                  error={!!errors.vacationDiscountConditions}
                  helperText={errors.vacationDiscountConditions?.message}
                />
              )}
            />
          </Grid>
        )}

        {/* vacationAverages — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={vacationAverages === "3" ? 6 : 12}>
              <Controller
                name="vacationAverages"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.vacationAverages}>
                    <InputLabel id="vacation-averages-label">Médias</InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="vacation-averages-label"
                      label="Médias"
                    >
                      {businessRuleOptions.vacationAverages.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.vacationAverages?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {vacationAverages === "3" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="vacationAveragesOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Informar"
                      size="small"
                      fullWidth
                      error={!!errors.vacationAveragesOther}
                      helperText={errors.vacationAveragesOther?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* vacationBenefits — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={["1", "4"].includes(vacationBenefits) ? 6 : 12}>
              <Controller
                name="vacationBenefits"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.vacationBenefits}>
                    <InputLabel id="vacation-benefits-label">
                      Existe benefício concedido quando do cálculo das férias
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="vacation-benefits-label"
                      label="Existe benefício concedido quando do cálculo das férias"
                    >
                      {businessRuleOptions.vacationBenefits.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.vacationBenefits?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {vacationBenefits === "1" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="vacationBenefitsList"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Informe quais são os benefícios"
                      size="small"
                      fullWidth
                      error={!!errors.vacationBenefitsList}
                      helperText={errors.vacationBenefitsList?.message}
                    />
                  )}
                />
              </Grid>
            )}
            {vacationBenefits === "4" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="vacationBenefitsDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Descreva a Regra de Negócio:"
                      size="small"
                      fullWidth
                      error={!!errors.vacationBenefitsDescription}
                      helperText={errors.vacationBenefitsDescription?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* terminationDiscounts — Autocomplete múltiplo com checkboxes */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={terminationDiscounts?.some(o => o.value === "4") ? 6 : 12}>
              <Controller
                name="terminationDiscounts"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={businessRuleOptions.terminationDiscounts}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={field.value || []}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    disabled={businessRuleIsValidated}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} size="small" />
                        {option.label}
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Descontos em rescisão"
                        size="small"
                        error={!!errors.terminationDiscounts}
                        helperText={errors.terminationDiscounts?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            {terminationDiscounts?.some(o => o.value === "4") && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="terminationDiscountsOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Especificar:"
                      size="small"
                      fullWidth
                      error={!!errors.terminationDiscountsOther}
                      helperText={errors.terminationDiscountsOther?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* loanDiscountPercentageTermination — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={loanDiscountPercentageTermination === "3" ? 6 : 12}>
              <Controller
                name="loanDiscountPercentageTermination"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.loanDiscountPercentageTermination}>
                    <InputLabel id="loan-discount-percentage-termination-label">
                      (%) Desconto Empréstimo(*):
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="loan-discount-percentage-termination-label"
                      label="(%) Desconto Empréstimo(*):"
                    >
                      {businessRuleOptions.loanDiscountPercentageTermination.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.loanDiscountPercentageTermination?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {loanDiscountPercentageTermination === "3" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="loanDiscountPercentageTerminationOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Especificar:"
                      size="small"
                      fullWidth
                      error={!!errors.loanDiscountPercentageTerminationOther}
                      helperText={errors.loanDiscountPercentageTerminationOther?.message}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography sx={footnoteTextSx}>
                (*) Lei nº 10.820/2003 , art. 1º , § 1º; Decreto nº 4.840/2003 , art. 13
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* terminationAverages — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={terminationAverages === "3" ? 6 : 12}>
              <Controller
                name="terminationAverages"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.terminationAverages}>
                    <InputLabel id="termination-averages-label">
                      Médias em rescisão
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="termination-averages-label"
                      label="Médias em rescisão"
                    >
                      {businessRuleOptions.terminationAverages.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.terminationAverages?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {terminationAverages === "3" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="terminationAveragesOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Informar"
                      size="small"
                      fullWidth
                      error={!!errors.terminationAveragesOther}
                      helperText={errors.terminationAveragesOther?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* payrollHasInterns — sem dependente direto */}
        <Grid item xs={12}>
          <Controller
            name="payrollHasInterns"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.payrollHasInterns}>
                <InputLabel id="payroll-has-interns-label">
                  Estagiários - Folha de Pagamento
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="payroll-has-interns-label"
                  label="Estagiários - Folha de Pagamento"
                >
                  {businessRuleOptions.payrollHasInterns.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.payrollHasInterns?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {payrollHasInterns === "1" && (
          <>
            {/* internSalaryAdjustmentRule — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={internSalaryAdjustmentRule === "3" ? 6 : 12}>
                  <Controller
                    name="internSalaryAdjustmentRule"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.internSalaryAdjustmentRule}>
                        <InputLabel id="intern-salary-adjustment-rule-label">
                          Estagiários - Reajuste:
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="intern-salary-adjustment-rule-label"
                          label="Estagiários - Reajuste:"
                        >
                          {businessRuleOptions.internSalaryAdjustmentRule.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.internSalaryAdjustmentRule?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {internSalaryAdjustmentRule === "3" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="internSalaryAdjustmentOther"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!errors.internSalaryAdjustmentOther}
                          helperText={errors.internSalaryAdjustmentOther?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* internHasDeductions — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={internHasDeductions === "1" ? 6 : 12}>
                  <Controller
                    name="internHasDeductions"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.internHasDeductions}>
                        <InputLabel id="intern-has-deductions-label">
                          Efetua descontos para estagiário
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="intern-has-deductions-label"
                          label="Efetua descontos para estagiário"
                        >
                          {businessRuleOptions.internHasDeductions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.internHasDeductions?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {internHasDeductions === "1" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="internDeductionsDescription"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva verbas | rubricas"
                          size="small"
                          fullWidth
                          error={!!errors.internDeductionsDescription}
                          helperText={errors.internDeductionsDescription?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* internHasBenefits — sem dependente direto */}
            <Grid item xs={12}>
              <Controller
                name="internHasBenefits"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.internHasBenefits}>
                    <InputLabel id="intern-has-benefits-label">
                      Paga-se benefício para estagiários?
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="intern-has-benefits-label"
                      label="Paga-se benefício para estagiários?"
                    >
                      {businessRuleOptions.internHasBenefits.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.internHasBenefits?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </>
        )}

        {payrollHasInterns === "1" && internHasBenefits === "1" && (
          <>
            {/* interBenefitsCalculation + interPayrollProcessing — sem dependentes */}
            <Grid item xs={12} md={6}>
              <Controller
                name="interBenefitsCalculation"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.interBenefitsCalculation}>
                    <InputLabel id="inter-benefits-calculation-label">
                      Via cálculo de Benefícios (regras abaixo)
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="inter-benefits-calculation-label"
                      label="Via cálculo de Benefícios (regras abaixo)"
                    >
                      {businessRuleOptions.interBenefitsCalculation.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.interBenefitsCalculation?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="interPayrollProcessing"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.interPayrollProcessing}>
                    <InputLabel id="inter-payroll-processing-label">
                      Via Folha de Pagamento
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="inter-payroll-processing-label"
                      label="Via Folha de Pagamento"
                    >
                      {businessRuleOptions.interPayrollProcessing.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.interPayrollProcessing?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* internFixedBenefit — dois dependentes alternativos na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={["1", "2"].includes(internFixedBenefit) ? 6 : 12}>
                  <Controller
                    name="internFixedBenefit"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.internFixedBenefit}>
                        <InputLabel id="intern-fixed-benefit-label">Fixo</InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="intern-fixed-benefit-label"
                          label="Fixo"
                        >
                          {businessRuleOptions.internFixedBenefit.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.internFixedBenefit?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {internFixedBenefit === "1" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="internFixedBenefitRubric"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva verbas | rubricas"
                          size="small"
                          fullWidth
                          error={!!errors.internFixedBenefitRubric}
                          helperText={errors.internFixedBenefitRubric?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
                {internFixedBenefit === "2" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="internFixedBenefitRule"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!errors.internFixedBenefitRule}
                          helperText={errors.internFixedBenefitRule?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* internBenefitValue — sem dependente */}
            <Grid item xs={12}>
              <Controller
                name="internBenefitValue"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.internBenefitValue}>
                    <InputLabel id="intern-benefit-value-label">
                      Valor do benefício
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="intern-benefit-value-label"
                      label="Valor do benefício"
                    >
                      {businessRuleOptions.internBenefitValue.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.internBenefitValue?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* internProportionalCalcRule — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={internProportionalCalcRule === "4" ? 6 : 12}>
                  <Controller
                    name="internProportionalCalcRule"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.internProportionalCalcRule}>
                        <InputLabel id="intern-proportional-calc-rule-label">
                          Quando proporcional, em meses com 28|29|31 dias divide-se por 28|29|31
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="intern-proportional-calc-rule-label"
                          label="Quando proporcional, em meses com 28|29|31 dias divide-se por 28|29|31"
                        >
                          {businessRuleOptions.internProportionalCalcRule.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.internProportionalCalcRule?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {internProportionalCalcRule === "4" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="internProportionalCalcRuleOther"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Outro, divide-se por"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ min: 1, max: 31 }}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, "");
                            const num = parseInt(val, 10);
                            if (val === "" || (num >= 1 && num <= 31)) {
                              field.onChange(val);
                            }
                          }}
                          error={!!errors.internProportionalCalcRuleOther}
                          helperText={errors.internProportionalCalcRuleOther?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* payrollHasApprentices — sem dependente direto */}
        <Grid item xs={12}>
          <Controller
            name="payrollHasApprentices"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.payrollHasApprentices}>
                <InputLabel id="payroll-has-apprentices-label">
                  Aprendizes - Folha de Pagamento
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="payroll-has-apprentices-label"
                  label="Aprendizes - Folha de Pagamento"
                >
                  {businessRuleOptions.payrollHasApprentices.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.payrollHasApprentices?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {payrollHasApprentices === "1" && (
          <>
            {/* apprenticeSalaryAdjustmentRule — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={apprenticeSalaryAdjustmentRule === "3" ? 6 : 12}>
                  <Controller
                    name="apprenticeSalaryAdjustmentRule"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.apprenticeSalaryAdjustmentRule}>
                        <InputLabel id="apprentice-salary-adjustment-rule-label">
                          Aprendizes - Reajuste:
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="apprentice-salary-adjustment-rule-label"
                          label="Aprendizes - Reajuste:"
                        >
                          {businessRuleOptions.apprenticeSalaryAdjustmentRule.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.apprenticeSalaryAdjustmentRule?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {apprenticeSalaryAdjustmentRule === "3" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="apprenticeSalaryAdjustmentOther"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!errors.apprenticeSalaryAdjustmentOther}
                          helperText={errors.apprenticeSalaryAdjustmentOther?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* apprenticeHasDeductions — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={apprenticeHasDeductions === "1" ? 6 : 12}>
                  <Controller
                    name="apprenticeHasDeductions"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.apprenticeHasDeductions}>
                        <InputLabel id="apprentice-has-deductions-label">
                          Efetua descontos para aprendiz
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="apprentice-has-deductions-label"
                          label="Efetua descontos para aprendiz"
                        >
                          {businessRuleOptions.apprenticeHasDeductions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.apprenticeHasDeductions?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {apprenticeHasDeductions === "1" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="apprenticeDeductionsDescription"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva verbas | rubricas"
                          size="small"
                          fullWidth
                          error={!!errors.apprenticeDeductionsDescription}
                          helperText={errors.apprenticeDeductionsDescription?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* apprenticeHasBenefits — sem dependente direto */}
            <Grid item xs={12}>
              <Controller
                name="apprenticeHasBenefits"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.apprenticeHasBenefits}>
                    <InputLabel id="apprentice-has-benefits-label">
                      Paga-se benefício para aprendizes?
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="apprentice-has-benefits-label"
                      label="Paga-se benefício para aprendizes?"
                    >
                      {businessRuleOptions.apprenticeHasBenefits.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.apprenticeHasBenefits?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </>
        )}

        {payrollHasApprentices === "1" && apprenticeHasBenefits === "1" && (
          <>
            {/* apprenticeBenefitsCalculation + apprenticePayrollProcessing — sem dependentes */}
            <Grid item xs={12} md={6}>
              <Controller
                name="apprenticeBenefitsCalculation"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.apprenticeBenefitsCalculation}>
                    <InputLabel id="apprentice-benefits-calculation-label">
                      Via cálculo de Benefícios (regras abaixo)
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="apprentice-benefits-calculation-label"
                      label="Via cálculo de Benefícios (regras abaixo)"
                    >
                      {businessRuleOptions.apprenticeBenefitsCalculation.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.apprenticeBenefitsCalculation?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="apprenticePayrollProcessing"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.apprenticePayrollProcessing}>
                    <InputLabel id="apprentice-payroll-processing-label">
                      Via Folha de Pagamento
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="apprentice-payroll-processing-label"
                      label="Via Folha de Pagamento"
                    >
                      {businessRuleOptions.apprenticePayrollProcessing.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.apprenticePayrollProcessing?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* apprenticeFixedBenefit — dois dependentes alternativos na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={["1", "2"].includes(apprenticeFixedBenefit) ? 6 : 12}>
                  <Controller
                    name="apprenticeFixedBenefit"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.apprenticeFixedBenefit}>
                        <InputLabel id="apprentice-fixed-benefit-label">Fixo</InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="apprentice-fixed-benefit-label"
                          label="Fixo"
                        >
                          {businessRuleOptions.apprenticeFixedBenefit.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.apprenticeFixedBenefit?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {apprenticeFixedBenefit === "1" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="apprenticeFixedBenefitRubric"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva verbas | rubricas"
                          size="small"
                          fullWidth
                          error={!!errors.apprenticeFixedBenefitRubric}
                          helperText={errors.apprenticeFixedBenefitRubric?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
                {apprenticeFixedBenefit === "2" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="apprenticeFixedBenefitRule"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!errors.apprenticeFixedBenefitRule}
                          helperText={errors.apprenticeFixedBenefitRule?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* apprenticeBenefitValue — sem dependente */}
            <Grid item xs={12}>
              <Controller
                name="apprenticeBenefitValue"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.apprenticeBenefitValue}>
                    <InputLabel id="apprentice-benefit-value-label">
                      Valor do benefício
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="apprentice-benefit-value-label"
                      label="Valor do benefício"
                    >
                      {businessRuleOptions.apprenticeBenefitValue.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.apprenticeBenefitValue?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* apprenticeProportionalCalcRule — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={apprenticeProportionalCalcRule === "4" ? 6 : 12}>
                  <Controller
                    name="apprenticeProportionalCalcRule"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.apprenticeProportionalCalcRule}>
                        <InputLabel id="apprentice-proportional-calc-rule-label">
                          Quando proporcional, em meses com 28|29|31 dias divide-se por 28|29|31
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="apprentice-proportional-calc-rule-label"
                          label="Quando proporcional, em meses com 28|29|31 dias divide-se por 28|29|31"
                        >
                          {businessRuleOptions.apprenticeProportionalCalcRule.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.apprenticeProportionalCalcRule?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {apprenticeProportionalCalcRule === "4" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="apprenticeProportionalCalcRuleOther"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Outro, divide-se por"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ min: 1, max: 31 }}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, "");
                            const num = parseInt(val, 10);
                            if (val === "" || (num >= 1 && num <= 31)) {
                              field.onChange(val);
                            }
                          }}
                          error={!!errors.apprenticeProportionalCalcRuleOther}
                          helperText={errors.apprenticeProportionalCalcRuleOther?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* hasAutonomous — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={hasAutonomous === "1" ? 6 : 12}>
              <Controller
                name="hasAutonomous"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.hasAutonomous}>
                    <InputLabel id="has-autonomous-label">
                      Contribuinte Individual - Autônomos
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="has-autonomous-label"
                      label="Contribuinte Individual - Autônomos"
                    >
                      {businessRuleOptions.hasAutonomous.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.hasAutonomous?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {hasAutonomous === "1" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="autonomousBusinessRule"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Contribuinte Individual - Autônomos - Descreva a Regra de Negócio"
                      size="small"
                      fullWidth
                      error={!!errors.autonomousBusinessRule}
                      helperText={errors.autonomousBusinessRule?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* hasProLabore — sem dependente direto */}
        <Grid item xs={12}>
          <Controller
            name="hasProLabore"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.hasProLabore}>
                <InputLabel id="has-pro-labore-label">
                  Contribuinte Individual - Pró-labore
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="has-pro-labore-label"
                  label="Contribuinte Individual - Pró-labore"
                >
                  {businessRuleOptions.hasProLabore.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.hasProLabore?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {hasProLabore === "1" && (
          <>
            {/* proLaboreSalaryAdjustmentRule — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={proLaboreSalaryAdjustmentRule === "3" ? 6 : 12}>
                  <Controller
                    name="proLaboreSalaryAdjustmentRule"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.proLaboreSalaryAdjustmentRule}>
                        <InputLabel id="pro-labore-salary-adjustment-rule-label">
                          Contribuinte Individual - Pró-labore - Reajuste
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="pro-labore-salary-adjustment-rule-label"
                          label="Contribuinte Individual - Pró-labore - Reajuste"
                        >
                          {businessRuleOptions.proLaboreSalaryAdjustmentRule.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.proLaboreSalaryAdjustmentRule?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {proLaboreSalaryAdjustmentRule === "3" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="proLaboreSalaryAdjustmentOther"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!errors.proLaboreSalaryAdjustmentOther}
                          helperText={errors.proLaboreSalaryAdjustmentOther?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* proLaboreHasFgtsNoLink — sem dependente */}
            <Grid item xs={12}>
              <Controller
                name="proLaboreHasFgtsNoLink"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.proLaboreHasFgtsNoLink}>
                    <InputLabel id="pro-labore-has-fgts-no-link-label">
                      Contribuinte Individual - Pró-labore - Com FGTS sem vínculo
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="pro-labore-has-fgts-no-link-label"
                      label="Contribuinte Individual - Pró-labore - Com FGTS sem vínculo"
                    >
                      {businessRuleOptions.proLaboreHasFgtsNoLink.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.proLaboreHasFgtsNoLink?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </>
        )}

        {/* payrollAnalyticalReportOrder + payrollSyntheticReportOrder — sem dependentes */}
        <Grid item xs={12} md={6}>
          <Controller
            name="payrollAnalyticalReportOrder"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.payrollAnalyticalReportOrder}>
                <InputLabel id="payroll-analytical-report-order-label">
                  Relatório RH/DP Analítico por ordem de
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="payroll-analytical-report-order-label"
                  label="Relatório RH/DP Analítico por ordem de"
                >
                  {businessRuleOptions.payrollAnalyticalReportOrder.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.payrollAnalyticalReportOrder?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="payrollSyntheticReportOrder"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.payrollSyntheticReportOrder}>
                <InputLabel id="payroll-synthetic-report-order-label">
                  Relatório RH/DP Sintético por ordem de
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="payroll-synthetic-report-order-label"
                  label="Relatório RH/DP Sintético por ordem de"
                >
                  {businessRuleOptions.payrollSyntheticReportOrder.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.payrollSyntheticReportOrder?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {/* payrollNetReport + payrollNetReportExcel — sem dependentes */}
        <Grid item xs={12} md={6}>
          <Controller
            name="payrollNetReport"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.payrollNetReport}>
                <InputLabel id="payroll-net-report-label">
                  Relatório RH/DP Líquido - Consolidado (matriz | filial)
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="payroll-net-report-label"
                  label="Relatório RH/DP Líquido - Consolidado (matriz | filial)"
                >
                  {businessRuleOptions.payrollNetReport.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.payrollNetReport?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="payrollNetReportExcel"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.payrollNetReportExcel}>
                <InputLabel id="payroll-net-report-excel-label">
                  Relatório RH/DP Líquido no formato em Excel
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="payroll-net-report-excel-label"
                  label="Relatório RH/DP Líquido no formato em Excel"
                >
                  {businessRuleOptions.payrollNetReportExcel.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.payrollNetReportExcel?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {/* payrollAccountingSyntheticReportOrder — sem dependente */}
        <Grid item xs={12}>
          <Controller
            name="payrollAccountingSyntheticReportOrder"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.payrollAccountingSyntheticReportOrder}>
                <InputLabel id="payroll-accounting-synthetic-report-order-label">
                  Relatório Contabilidade Sintético por ordem de
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="payroll-accounting-synthetic-report-order-label"
                  label="Relatório Contabilidade Sintético por ordem de"
                >
                  {businessRuleOptions.payrollAccountingSyntheticReportOrder.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.payrollAccountingSyntheticReportOrder?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {/* hasDomesticEmployee — sem dependente direto */}
        <Grid item xs={12}>
          <Controller
            name="hasDomesticEmployee"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.hasDomesticEmployee}>
                <InputLabel id="has-domestic-employee-label">
                  Empregado Doméstico
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="has-domestic-employee-label"
                  label="Empregado Doméstico"
                >
                  {businessRuleOptions.hasDomesticEmployee.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.hasDomesticEmployee?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {hasDomesticEmployee === "1" && (
          <>
            {/* domesticEmployeeSalaryAdjustmentRule — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={domesticEmployeeSalaryAdjustmentRule === "3" ? 6 : 12}>
                  <Controller
                    name="domesticEmployeeSalaryAdjustmentRule"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.domesticEmployeeSalaryAdjustmentRule}>
                        <InputLabel id="domestic-employee-salary-adjustment-rule-label">
                          Empregado Doméstico Reajuste
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="domestic-employee-salary-adjustment-rule-label"
                          label="Empregado Doméstico Reajuste"
                        >
                          {businessRuleOptions.domesticEmployeeSalaryAdjustmentRule.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors.domesticEmployeeSalaryAdjustmentRule?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {domesticEmployeeSalaryAdjustmentRule === "3" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="domesticEmployeeSalaryAdjustmentRuleOther"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!errors.domesticEmployeeSalaryAdjustmentRuleOther}
                          helperText={errors.domesticEmployeeSalaryAdjustmentRuleOther?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* domesticEmployeeWorkTimeControl — sem dependente */}
            <Grid item xs={12}>
              <Controller
                name="domesticEmployeeWorkTimeControl"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.domesticEmployeeWorkTimeControl}>
                    <InputLabel id="domestic-employee-work-time-control-label">
                      Empregado Doméstico Realiza controle de jornada
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="domestic-employee-work-time-control-label"
                      label="Empregado Doméstico Realiza controle de jornada"
                    >
                      {businessRuleOptions.domesticEmployeeWorkTimeControl.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.domesticEmployeeWorkTimeControl?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* domesticEmployeeClosePeriod — dependente na mesma linha */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={domesticEmployeeClosePeriod === "4" ? 6 : 12}>
                  <Controller
                    name="domesticEmployeeClosePeriod"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" required error={!!errors.domesticEmployeeClosePeriod}>
                        <InputLabel id="domestic-employee-close-period-label">
                          Empregado Período de fechamento do ponto
                        </InputLabel>
                        <Select
                          {...field}
                          required disabled={businessRuleIsValidated}
                          labelId="domestic-employee-close-period-label"
                          label="Empregado Período de fechamento do ponto"
                        >
                          {businessRuleOptions.domesticEmployeeClosePeriod.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.domesticEmployeeClosePeriod?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {domesticEmployeeClosePeriod === "4" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="domesticEmployeeClosePeriodOther"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required disabled={businessRuleIsValidated}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!errors.domesticEmployeeClosePeriodOther}
                          helperText={errors.domesticEmployeeClosePeriodOther?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* hasSpecificWageItems — linha própria com tabela condicional abaixo */}
        <Grid item xs={12}>
          <Controller
            name="hasSpecificWageItems"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.hasSpecificWageItems}>
                <InputLabel id="has-specific-wage-items-label">
                  A empresa possui verbas | rubricas peculiares ao negócio
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="has-specific-wage-items-label"
                  label="A empresa possui verbas | rubricas peculiares ao negócio"
                >
                  {businessRuleOptions.hasSpecificWageItems.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.hasSpecificWageItems?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {hasSpecificWageItems === "1" && (
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                bgcolor: "grey.50",
              }}
            >
              <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>
                Verbas | Rubricas peculiares ao negócio
              </Typography>
              <AmountGridTable
                control={control}
                errors={errors}
                rows={specificWageRows}
                onAddRow={handleAddSpecificWageRow}
                onDeleteRow={handleDeleteSpecificWageRow}
                fieldNamespace="specificWageRows"
                isValidated={businessRuleIsValidated}
              />
            </Box>
          </Grid>
        )}

        {/* hasCustomWageIncidences — linha própria com tabela condicional abaixo */}
        <Grid item xs={12}>
          <Controller
            name="hasCustomWageIncidences"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.hasCustomWageIncidences}>
                <InputLabel id="has-custom-wage-incidences-label">
                  Paga-se Gratificação, Prêmio, Comissão, Ajuda de Custo, dentre outras previstas
                  na legislação com configuração das Incidências diverso da Legislação Trabalhista
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="has-custom-wage-incidences-label"
                  label="Paga-se Gratificação, Prêmio, Comissão, Ajuda de Custo, dentre outras previstas na legislação com configuração das Incidências diverso da Legislação Trabalhista"
                >
                  {businessRuleOptions.hasCustomWageIncidences.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.hasCustomWageIncidences?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {hasCustomWageIncidences === "1" && (
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                bgcolor: "grey.50",
              }}
            >
              <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>
                Incidências diversas da Legislação Trabalhista
              </Typography>
              <AmountGridTable
                control={control}
                errors={errors}
                rows={customWageRows}
                onAddRow={handleAddCustomWageRow}
                onDeleteRow={handleDeleteCustomWageRow}
                fieldNamespace="customWageRows"
                isValidated={businessRuleIsValidated}
              />
            </Box>
          </Grid>
        )}

        {/* hasHazardPay + hasInsalubrityPay — sem dependentes */}
        <Grid item xs={12} md={6}>
          <Controller
            name="hasHazardPay"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.hasHazardPay}>
                <InputLabel id="has-hazard-pay-label">Periculosidade</InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="has-hazard-pay-label"
                  label="Periculosidade"
                >
                  {businessRuleOptions.hasHazardPay.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.hasHazardPay?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="hasInsalubrityPay"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.hasInsalubrityPay}>
                <InputLabel id="has-insalubrity-pay-label">Insalubridade</InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="has-insalubrity-pay-label"
                  label="Insalubridade"
                >
                  {businessRuleOptions.hasInsalubrityPay.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.hasInsalubrityPay?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {(hasHazardPay === "1" || hasInsalubrityPay === "1") && (
          <Grid item xs={12}>
            <Controller
              name="discountAdditionalOnAbsence"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.discountAdditionalOnAbsence}>
                  <InputLabel id="discount-additional-on-absence-label">
                    Faltas injustificadas (desconto do adicional) - O empregado que
                    injustificadamente deixar de comparecer ao serviço, além do desconto do salário
                    do(s) dia(s) em que tiver faltado, sofrerá o desconto do adicional de
                    periculosidade | insalubridade proporcionalmente àquele(s) dia(s)
                  </InputLabel>
                  <Select
                    {...field}
                    required disabled={businessRuleIsValidated}
                    labelId="discount-additional-on-absence-label"
                    label="Faltas injustificadas (desconto do adicional) - O empregado que injustificadamente deixar de comparecer ao serviço, além do desconto do salário do(s) dia(s) em que tiver faltado, sofrerá o desconto do adicional de periculosidade | insalubridade proporcionalmente àquele(s) dia(s)"
                  >
                    {businessRuleOptions.discountAdditionalOnAbsence.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.discountAdditionalOnAbsence?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        )}

        {/* proportionalRuleForDifferentMonthDays — sem dependente */}
        <Grid item xs={12}>
          <Controller
            name="proportionalRuleForDifferentMonthDays"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.proportionalRuleForDifferentMonthDays}>
                <InputLabel id="proportional-rule-for-different-month-days-label">
                  Regra da Proporcionalidade - Meses com 28, 29 e 31 dias - A empresa aplica a
                  regra da proporcionalidade conforme base legal
                </InputLabel>
                <Select
                  {...field}
                  required disabled={businessRuleIsValidated}
                  labelId="proportional-rule-for-different-month-days-label"
                  label="Regra da Proporcionalidade - Meses com 28, 29 e 31 dias - A empresa aplica a regra da proporcionalidade conforme base legal"
                >
                  {businessRuleOptions.proportionalRuleForDifferentMonthDays.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.proportionalRuleForDifferentMonthDays?.message}</FormHelperText>
              </FormControl>
            )}
          />
          <Typography sx={footnoteTextSx}>
            (*) Consoante ao artigo 58 e 64, ambos da CLT, nos meses com número de dias distinto
            de 30 dias, é necessário aplicar o divisor pelo número de dias que possui o mês, ou
            seja, não é aplicado o divisor 30. O divisor do salário irá variar conforme o número
            de dias totais do mês, quando não trabalhado integralmente, podendo ser de 28, 29 ou
            31 dias.
          </Typography>
        </Grid>

        {/* otherExclusiveRules — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={otherExclusiveRules === "1" ? 6 : 12}>
              <Controller
                name="otherExclusiveRules"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.otherExclusiveRules}>
                    <InputLabel id="other-exclusive-rules-label">
                      Outras regras | informações exclusivas
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={businessRuleIsValidated}
                      labelId="other-exclusive-rules-label"
                      label="Outras regras | informações exclusivas"
                    >
                      {businessRuleOptions.otherExclusiveRules.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.otherExclusiveRules?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {otherExclusiveRules === "1" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="otherExclusiveRulesDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={businessRuleIsValidated}
                      label="Descreva a Regra de Negócio"
                      size="small"
                      fullWidth
                      error={!!errors.otherExclusiveRulesDescription}
                      helperText={errors.otherExclusiveRulesDescription?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}
